import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Panel } from "@/components/shared";
import { api, errMsg } from "@/lib/api";

function AiSettings({ s, onSaved }) {
  const [f, setF] = useState(s);
  const save = async () => {
    try { await api.put("/admin/settings", { model_heavy: f.model_heavy, model_light: f.model_light, global_monthly_budget_usd: Number(f.global_monthly_budget_usd),
      kill_switch: f.kill_switch, cache_ttl_hours: Number(f.cache_ttl_hours), infra_cost_per_user_usd: Number(f.infra_cost_per_user_usd) }); toast.success("AI settings saved"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const field = (k, t) => <div className="space-y-1"><Label className="text-xs">{t}</Label><Input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`ai-setting-${k}`} /></div>;
  return (
    <Panel testid="admin-ai-settings"><h3 className="mb-4 font-semibold">Model routing, budgets & caching</h3>
      <div className="grid gap-4 sm:grid-cols-3">{field("model_heavy", "Heavy-task model")}{field("model_light", "Light-task model")}{field("global_monthly_budget_usd", "Platform monthly AI budget (USD)")}
        {field("cache_ttl_hours", "Cache TTL (hours)")}{field("infra_cost_per_user_usd", "Infra cost / user / month (USD)")}
        <label className="flex items-center gap-3 pt-6 text-sm font-medium"><Switch checked={!!f.kill_switch} onCheckedChange={(v) => setF({ ...f, kill_switch: v })} data-testid="ai-kill-switch" />AI kill switch</label></div>
      <Button className="mt-4" onClick={save} data-testid="ai-settings-save-button">Save settings</Button>
    </Panel>
  );
}

export function Economics() {
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/economics").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  if (!d) return null;
  return (
    <div className="space-y-6">
      <Panel testid="admin-margin-chart"><h3 className="mb-4 font-semibold">MRR vs AI + infra cost by tier (this month)</h3>
        <div className="h-64"><ResponsiveContainer><BarChart data={d.tiers}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} /><Tooltip /><Legend />
          <Bar dataKey="mrr" name="MRR $" fill="hsl(224 76% 48%)" /><Bar dataKey="ai_cost" name="AI cost $" fill="hsl(38 92% 50%)" /><Bar dataKey="infra_cost" name="Infra $" fill="hsl(215 16% 60%)" /></BarChart></ResponsiveContainer></div></Panel>
      <Panel className="overflow-x-auto" testid="admin-margin-table">
        <Table><TableHeader><TableRow><TableHead>Tier</TableHead><TableHead>Price</TableHead><TableHead>Users</TableHead><TableHead>Paying</TableHead><TableHead>MRR</TableHead><TableHead>AI cost</TableHead><TableHead>Calls (cached)</TableHead><TableHead>Gross margin</TableHead></TableRow></TableHeader>
          <TableBody>{d.tiers.map((t) => (<TableRow key={t.plan_id}><TableCell>{t.name} <span className="text-xs text-muted-foreground">{t.audience}</span></TableCell><TableCell>{t.price == null ? "Custom" : `$${t.price}`}</TableCell>
            <TableCell>{t.users}</TableCell><TableCell>{t.paying}</TableCell><TableCell>${t.mrr}</TableCell><TableCell>${t.ai_cost.toFixed(4)}</TableCell><TableCell>{t.ai_calls} ({t.cache_hits})</TableCell>
            <TableCell className="font-mono">{t.gross_margin_pct == null ? "—" : `${t.gross_margin_pct}%`}</TableCell></TableRow>))}</TableBody></Table>
        <p className="mt-3 text-xs text-muted-foreground">{d.note}</p>
      </Panel>
      <AiSettings s={d.settings} onSaved={load} />
    </div>
  );
}

function PlanEditor({ plan, onSaved }) {
  const [ent, setEnt] = useState(plan.entitlements);
  const save = async () => { try { await api.put(`/admin/plans/${plan.plan_id}`, { entitlements: ent }); toast.success(`${plan.name} entitlements saved`); onSaved(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Panel testid={`entitlements-${plan.plan_id}`}>
      <div className="mb-3 flex items-baseline justify-between"><h3 className="font-semibold">{plan.name} <span className="text-xs text-muted-foreground">{plan.audience}</span></h3><span className="font-mono text-sm">{plan.price == null ? "Custom" : `$${plan.price}/mo (locked)`}</span></div>
      <div className="grid gap-3 sm:grid-cols-2">{Object.entries(ent).map(([k, v]) => (
        <div key={k} className="space-y-1"><Label className="text-xs">{k}</Label>
          {typeof v === "boolean" ? <Switch checked={v} onCheckedChange={(x) => setEnt({ ...ent, [k]: x })} data-testid={`ent-${plan.plan_id}-${k}`} /> :
            <Input type="number" value={v} onChange={(e) => setEnt({ ...ent, [k]: Number(e.target.value) })} data-testid={`ent-${plan.plan_id}-${k}`} />}</div>))}</div>
      <Button size="sm" className="mt-4" onClick={save} data-testid={`ent-save-${plan.plan_id}`}>Save</Button>
    </Panel>
  );
}

export function Entitlements() {
  const [plans, setPlans] = useState([]);
  const load = () => api.get("/admin/plans").then((r) => setPlans(r.data));
  useEffect(() => { load(); }, []);
  return <div className="grid gap-4 lg:grid-cols-2">{plans.map((p) => <PlanEditor key={p.plan_id} plan={p} onSaved={load} />)}</div>;
}
